import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { ContextMenuOptions } from 'src/app/components/context-menu-global/context-menu-global.component';
import { sleep } from 'src/app/lib/sleep';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ContextMenuService } from 'src/app/services/context-menu.service';

@Component({
  selector: 'app-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ContextMenuComponent implements OnDestroy {
  @Input() position = 'auto';
  @Input() verticalPosition = 'auto';
  @Input() options: ContextMenuOptions = {};
  @Input() name: string;
  @Input() contextHoverIdentifier: string;
  @Input() disabled: boolean;
  @Output() onToggle = new EventEmitter();
  @ViewChild('contextMenu') contextMenu: TemplateRef<unknown>;
  @ViewChild('contextButton') contextButton: ElementRef;
  public isOpen = false;
  public _isOpen = false;
  private hoverSub: Subscription;
  private hover: boolean;
  private hoverDelayStart: number;
  lastOpenTime: number;

  @HostListener('mouseenter') async enter() {
    if (this.disabled) return;
    if (ScreenSizes.screenSize() !== 'desktop') return;
    if (this.options.openOnHover) {
      this.hover = true;
      if (this.options.hoverDelay) {
        const start = Date.now();
        this.hoverDelayStart = start;
        await sleep(this.options.hoverDelay);
        if (this.hoverDelayStart !== start) return;
        if (!this.hover) return;
      }
      this.open();
    }
  }
  @HostListener('mouseleave') async leave() {
    if (this.disabled) return;
    if (ScreenSizes.screenSize() !== 'desktop') return;
    this.hover = false;
    if (this.lastOpenTime && Date.now() - this.lastOpenTime < 200) await sleep(200);
    await sleep(0);
    if (this.contextMenuService.hoveringOverContextContent) return;
    if (this.options.openOnHover) this.close();
  }

  constructor(
    private contextMenuService: ContextMenuService,
    private analytics: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    if (isPlatformServer(this.platformId)) return;
    this.hoverSub = this.contextMenuService.onContextContentHover.subscribe(async hover => {
      if (hover !== false) return;
      await sleep(0);
      if (this.hover) return;
      if (ScreenSizes.screenSize() !== 'desktop') return;
      if (this.options.openOnHover) this.close();
    });
  }

  clickOpen(event: MouseEvent) {
    if (this.disabled) return;
    if (!this.options.allowClicks) event.preventDefault();
    if (this.options.openOnHover && ScreenSizes.screenSize() === 'desktop') return;
    if (!this.options.allowClicks) event?.stopPropagation();
    if (this.isOpen) {
      return this.close();
    }
    this.open();
  }

  async open() {
    this.lastOpenTime = Date.now();
    if (this.isOpen) {
      this.close();
      if (!this._isOpen) {
        await sleep(200);
        return this.open();
      }
      return;
    }
    const options = { ...this.options };
    if (ScreenSizes.screenSize() !== 'desktop') delete options.openOnHover;

    const resp = await this.contextMenuService.showContext({
      component: this,
      template: this.contextMenu,
      position: this.position,
      verticalPosition: this.verticalPosition,
      clientEl: this.contextButton.nativeElement,
      options,
      name: this.name,
      contextHoverIdentifier: this.contextHoverIdentifier,
    });
    if (resp) {
      this.isOpen = true;
      this._isOpen = true;
      this.onToggle.emit(true);
    }
  }

  async close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    if (this.name) this.analytics.track('close_context:' + this.name, false);
    this.contextMenuService.hideContext();
    this.onToggle.emit(false);
    await sleep(200);
    this.isOpen = false;
  }

  ngOnDestroy() {
    this.hoverSub?.unsubscribe();
  }
}
